import Collapsible from 'components/collapsible';
import DatePicker from 'components/date-picker';
import FilterSidebar from 'components/filter-sidebar';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import { useAccountState } from 'contexts/account/account-context';
// import { useSitesState } from 'contexts/sites/SitesContext'; re-enable and replace account.company.addressAccess after sites endpoint is accurately returning stuff (end of 22.7 sprint?)
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import Paper from 'components/paper';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import dayjs, { Dayjs } from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighChartsExporting from 'highcharts/modules/exporting';
import HighChartsOfflineExporting from 'highcharts/modules/offline-exporting';
import { useGet } from 'hooks/use-get';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useJsonToCsv } from 'react-json-csv';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import googleTagManager from 'utils/google-tag-manager';
import slugify from 'utils/slugify';
import { useSitesState } from 'views/order-feed/context/sites-context';
import { getPurchaseHistory } from 'views/order-feed/features/purchase-history/api/get-purchase-history';
import { GetPurchaseHistoryInput } from 'views/order-feed/features/purchase-history/types/get-purchase-history-input.type';
import { SiteAndGroupPurchaseHistory } from 'views/order-feed/features/purchase-history/types/purchase-history.type';
import { Site } from 'views/order-feed/types/site.type';
import { OrderFeedParams } from 'views/order-feed/types/order-feed-params.type';
import { OrderFeedProvider } from 'views/order-feed/context/order-feed-context';
import OrderFeedTabsHeader from '../../components/order-feed-tabs-header';
import { SiteAutocomplete } from 'views/order-feed/components/site-autocomplete';

type Props = {};

const today = dayjs();

const PurchaseHistory: React.FC<Props> = () => {
  HighChartsExporting(Highcharts);
  HighChartsOfflineExporting(Highcharts);

  const { account } = useAccountState();
  const intl = useIntl();
  const params = useParams<OrderFeedParams>();
  const history = useHistory();
  const theme = useTheme();
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  const {
    sitesQuery: { data: sites },
  } = useSitesState();
  const PAGE_NAME_ID = 'orderFeed.purchaseHistory';

  // #region STATE
  const [title, setTitle] = useState<string>('');
  const [groupType, setGroupType] = useState<string>('family'); // product | family
  const [values, setValues] = useState<Site[]>([]);
  const { saveAsCsv } = useJsonToCsv();
  const [monthView, setMonthView] = useState<number>(3);
  const [fromDate, setFromDate] = useState<Dayjs>(dayjs(today).subtract(monthView, 'months').startOf('month'));
  const [toDate, setToDate] = useState<Dayjs>(today.endOf('month'));
  const [monthYears, setMonthYears] = useState<string[]>([]);
  const [series, setSeries] = useState<any>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setValues(params.globalSiteId ? sites?.filter((site) => site.globalSiteId === params.globalSiteId) || [] : []);
  }, [params.globalSiteId, sites]);

  // #endregion STATE

  // #region FETCH DATA

  const productAnalyticsParams: GetPurchaseHistoryInput = useMemo(() => {
    return {
      startDate: dayjs(fromDate).startOf('month').toISOString(),
      endDate: dayjs(toDate).endOf('month').toISOString(),
      groupingType: groupType,
      siteIds: _.uniq(values.map((site) => site.globalSiteId)),
    };
  }, [fromDate, groupType, monthView, toDate, values]);

  const { data, isLoading } = useGet(
    ['getProductAnalytics', account.company!.companyId, productAnalyticsParams],
    ({ queryKey }) => getPurchaseHistory(String(queryKey[1]), productAnalyticsParams),
    {
      enabled:
        (account.company?.features?.hasOrderFeed ||
          account.company?.features?.hasOrderFreeDelivery ||
          account.company?.features?.hasOrderRead) &&
        !!account.company?.companyId &&
        !!values.length &&
        !!fromDate,
    }
  );

  // #endregion FETCH DATA

  // #region RENDERS

  const renderProductRows = () => {
    const rows = _.chain(data?.perSiteAndGroupPurchasesHistory)
      .sortBy((siteHistory) => siteHistory.name)
      .value();

    return rows;
  };

  // #endregion RENDERS

  Highcharts.setOptions({
    colors: [
      '#017F62',
      '#005B83',
      '#DA6A07',
      '#7D61B8',
      '#4C6475',
      '#B22B95',
      '#847F0F',
      '#A63C25',
      '#0F3084',
      '#1E828F',
      '#84286A',
      '#17723B',
      '#A05B68',
      '#2568A6',
      '#7F6C5B',
      '#6A2C9B',
      '#C31535',
      '#6A081A',
    ],
  });

  const commonStyle = {
    fontFamily: 'DM Sans, Arial',
    color: theme.palette.darkBlue.main,
    fontSize: '12px',
  };

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      style: {
        ...commonStyle,
      },
    },
    credits: {
      enabled: false,
    },
    legend: {
      itemStyle: {
        ...commonStyle,
      },
    },
    title: {
      text: '.',
      style: {
        fontFamily: 'DM Sans, Arial',
        fill: 'white',
        color: 'white',
      },
    },
    xAxis: {
      categories: monthYears,
      labels: {
        style: {
          ...commonStyle,
        },
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: intl.formatMessage({ id: `${PAGE_NAME_ID}.chartQuantity` }),
        style: {
          ...commonStyle,
          fontSize: '14px',
          textTransform: 'none',
        },
      },
      labels: {
        style: {
          ...commonStyle,
        },
      },
      stackLabels: {
        enabled: true,
        style: {
          ...commonStyle,
          fontWeight: '500',
          fontSize: '16px',
        },
      },
    },
    exporting: {
      filename: title,
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
      chartOptions: {
        plotOptions: {
          series: {
            dataLabels: {
              enabled: false,
            },
          },
        },
      },
      fallbackToExportServer: false,
    },
    plotOptions: {
      series: {
        stacking: 'normal',
      },
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: false,
        },
      },
    },
    series: series,
  } as Highcharts.Options;

  const renderHeaders = () => {
    let arr: HeadCell[] = [
      {
        id: `product_group`,
        label:
          groupType === 'product'
            ? intl.formatMessage({ id: `${PAGE_NAME_ID}.product` })
            : intl.formatMessage({ id: `${PAGE_NAME_ID}.family` }),
        align: 'left',
        sticky: true,
      },
    ];
    let currentYear = dayjs().format('YYYY');

    monthYears.forEach((m, idx) => {
      let [year, month] = m.split('-');
      if (month === '12' && idx !== monthYears.length) {
        arr.push({ id: m, label: dayjs(m, 'YYYY-MM').format('MMM'), align: 'right' });
        arr.push({ id: `${year}-total`, label: year, align: 'right' });
      } else {
        arr.push({ id: m, label: dayjs(m, 'YYYY-MM').format('MMM'), align: 'right' });
      }
    });
    arr.push({ id: `${currentYear}-total`, label: currentYear, align: 'right' });
    arr.push({ id: `all-total`, label: intl.formatMessage({ id: `${PAGE_NAME_ID}.total` }), align: 'right' });
    return arr;
  };

  // #endregion RENDERS

  // #region HELPERS

  const getCorrectMonthList = useCallback(() => {
    const timeValues: string[] = [];
    let interim = dayjs(fromDate);

    while (dayjs(toDate) > interim || interim.format('M') === dayjs(toDate).format('M')) {
      timeValues.push(interim.format('YYYY-MM'));
      interim = interim.add(1, 'month');
    }

    return timeValues;
  }, [fromDate, toDate]);

  const totalMonthlyQuantity = (site: SiteAndGroupPurchaseHistory, yearMonth: string) => {
    let groups = site.perGroupPurchasesHistory;
    let [year, month] = yearMonth.split('-');

    let allMonthlyTotals = groups.map((group) => group.monthlyTotals);

    let total = allMonthlyTotals.reduce(
      (total, mt) => total + (mt.find((mon) => mon.month === Number(month) && mon.year === Number(year))?.quantity || 0),
      0
    );

    return total;
  };

  const totalYearlyQuantity = (site: SiteAndGroupPurchaseHistory, y: number) => {
    let groups = site.perGroupPurchasesHistory;

    let yearlyTotals = groups.map((group) => group.yearlyTotals);

    let total = yearlyTotals.reduce((total, mt) => total + (mt.find((mon) => mon.year === y)?.quantity || 0), 0);

    return total;
  };

  // custom csv export because chart does not use per site data, so it does not exist in chart csv export
  const exportToCSV = () => {
    let obArr: any[] = [];
    let monYears: string[] = [];
    monthYears.forEach((my, idx) => {
      if (my.split('-')[1] === '12' && idx !== monthYears.length) {
        monYears.push(my);
        monYears.push(`${my.split('-')[0]}-total`);
      } else {
        monYears.push(my);
      }
    });

    monYears.push(`${dayjs().format('YYYY')}-total`); // current year
    let allMonthlyTotals: number[] = monYears.map(() => 0);

    let emptyRow = monYears.reduce((header, monthYear) => ({ ...header, [monthYear]: '' }), { name: '', total: '' });
    // loop through each site, create array to hold data
    // export 2d array to csv
    let monthHeaders = monYears.reduce(
      (header, monthYear) =>
        monthYear.includes('total')
          ? {
              ...header,
              [monthYear]: monthYear.split('-')[0],
            }
          : { ...header, [monthYear]: monthYear },
      { name: 'Name' }
    );

    Object.assign(monthHeaders, { total: 'Total' });

    _.chain(data?.perSiteAndGroupPurchasesHistory)
      .sortBy((site) => site.name)
      .value()
      .forEach((site, idx) => {
        let groups = site.perGroupPurchasesHistory.map((group) => group);

        let siteRow = monYears.reduce(
          (siteObj, monthYear, idxx) => {
            let quantity = 0;
            if (monthYear.includes('total')) {
              quantity = totalYearlyQuantity(site, Number(monthYear.split('-')[0]));
            } else {
              quantity = totalMonthlyQuantity(site, monthYear);
            }

            allMonthlyTotals[idxx] = allMonthlyTotals[idxx] + quantity;

            return {
              ...siteObj,
              [monthYear]: quantity,
            };
          },

          { name: site.name }
        );

        Object.assign(siteRow, { total: groups.reduce((a, g) => a + (g.total || 0), 0) });
        idx > 1 && obArr.push(emptyRow); // second site or more to assist with spacing / readability
        obArr.push(siteRow);

        let groupYearlyTotal = 0;
        let groupTotal = 0;
        groups.forEach((g) => {
          let groupRow = monYears.reduce(
            (my, monthYear) => {
              let [year, month] = monthYear.split('-');
              let mt = g.monthlyTotals.find((mt) => mt.year === Number(year) && mt.month === Number(month))?.quantity || 0;
              groupYearlyTotal = groupYearlyTotal + mt;
              groupTotal = groupTotal + mt;
              if (monthYear.includes('total')) {
                let ob = { ...my, [monthYear]: groupYearlyTotal };
                groupYearlyTotal = 0; // new year, set total to 0
                return ob;
              } else {
                return { ...my, [monthYear]: mt };
              }
            },
            { name: g.name }
          );
          Object.assign(groupRow, { total: groupTotal });
          obArr.push(groupRow);
          groupTotal = 0;
          groupYearlyTotal = 0; // next group, reset total to 0
        });
        idx === 0 && obArr.push(emptyRow);
      });

    obArr.push(emptyRow);

    let groupTotalTitle = intl.formatMessage({ id: `${PAGE_NAME_ID}.family` }) + ' Total';

    obArr.push(
      monYears.reduce((emptyCells, monthYear) => ({ ...emptyCells, [monthYear]: '' }), {
        name: groupTotalTitle,
        total: '',
      })
    );

    let allGroupsTotal = 0;

    data?.perGroupPurchasesHistory.forEach((group) => {
      allGroupsTotal = allGroupsTotal + group.total;
      let totalGroupRow = monYears.reduce(
        (totals, monthYear) => {
          let quantity = 0;
          let [year, month] = monthYear.split('-');
          if (monthYear.includes('total')) {
            quantity = group.monthlyTotals.reduce((total, mt) => total + (mt.year === Number(year) ? mt.quantity : 0), 0);
          } else {
            quantity = group.monthlyTotals.reduce(
              (total, mt) => total + (mt.month === Number(month) && mt.year === Number(year) ? mt.quantity : 0),
              0
            );
          }

          return {
            ...totals,
            [monthYear]: quantity,
          };
        },
        { name: group.name, total: group.total }
      );

      obArr.push(totalGroupRow);
    });

    obArr.push(emptyRow);

    obArr.push(
      monYears.reduce((allGroupTotals, monthYear, idx) => ({ ...allGroupTotals, [monthYear]: allMonthlyTotals[idx] }), {
        name: 'Total',
        total: allGroupsTotal,
      })
    );

    saveAsCsv({
      data: obArr,
      fields: monthHeaders,
      filename: title,
    });

    googleTagManager.push({
      event: 'download',
      fileName: title,
      fileType: 'csv',
      siteId: params.globalSiteId,
      pageUrl: window.location.pathname,
    });
  };

  // #endregion HELPERS

  // #region HOOKS

  useEffect(() => {
    if (!isLoading) {
      let t = data?.perGroupPurchasesHistory.length
        ? data?.perGroupPurchasesHistory.map((group) => ({
            name: group.name,
            data: monthYears.map((my) => {
              let y =
                group.monthlyTotals.find((mt) => mt.year === Number(my.split('-')[0]) && mt.month === Number(my.split('-')[1]))
                  ?.quantity || null;

              return {
                name: my,
                y: y,
              };
            }),
          }))
        : [{ name: intl.formatMessage({ id: `${PAGE_NAME_ID}.noResults` }), data: monthYears.map((my) => ({ name: my, y: null })) }];

      setSeries(t);
    }
  }, [data, intl, isLoading, monthYears]);

  useEffect(() => {
    if (!!fromDate && !!toDate) setMonthYears(getCorrectMonthList());
  }, [fromDate, getCorrectMonthList, toDate]);

  useEffect(() => {
    setTitle(`${slugify(intl.formatMessage({ id: `${PAGE_NAME_ID}.title` }))}-${dayjs().format('L')}`);
  }, [intl]);

  // #endregion HOOKS

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <OrderFeedTabsHeader
        utils={
          <Breadcrumbs
            crumbs={
              params.siteErpId
                ? [
                    {
                      text:
                        account.company?.addressAccess.find((s) => s.siteErpId === params.siteErpId)?.name ||
                        params.siteErpId,
                      url: `${params.siteErpId}/${params.globalSiteId}`,
                    },
                  ]
                : undefined
            }
          />
        }
      />
      <Box display={'flex'} justifyContent={'flex-end'}>
        <SiteAutocomplete multiple values={values} onChange={setValues} route={{ basePath: '/order-feed/purchase-history' }} />
      </Box>
      <Paper transparent>
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <FilterSidebar
            clearFilters={() => {
              setGroupType('family');
              setMonthView(3);
              setFromDate(today.subtract(3, 'months'));
              setToDate(today);
            }}
            showClearFilter={
              monthView !== 3 || !fromDate.isSame(today.subtract(3, 'months').startOf('month')) || !toDate.isSame(today.endOf('month'))
            }>
            <Select
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.groupType` })}
              value={groupType}
              onChange={(event) => {
                let value = event.target.value as string;
                setGroupType(value);
              }}>
              <RadioSelectItem value={'product'}>{intl.formatMessage({ id: `${PAGE_NAME_ID}.product` })}</RadioSelectItem>
              <RadioSelectItem value={'family'}>{intl.formatMessage({ id: `${PAGE_NAME_ID}.family` })}</RadioSelectItem>
            </Select>

            <Select
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.monthView` })}
              value={monthView}
              onChange={(event) => {
                let value = event.target.value as number;
                setMonthView(value);
                setFromDate(dayjs(today).subtract(value, 'months'));
                setToDate(today);
              }}>
              <RadioSelectItem value={'1'}>{intl.formatMessage({ id: `${PAGE_NAME_ID}.pastMonth` })}</RadioSelectItem>
              <RadioSelectItem value={'3'}>{intl.formatMessage({ id: `${PAGE_NAME_ID}.pastThreeMonth` })}</RadioSelectItem>
              <RadioSelectItem value={'6'}>{intl.formatMessage({ id: `${PAGE_NAME_ID}.pastSixMonth` })}</RadioSelectItem>
            </Select>

            <DatePicker
              fullWidth={isMobile}
              label={intl.formatMessage({ id: `orderFeed.listOrderSupplyPlan.filter.fromDateLabel` })}
              disableFuture={true}
              value={fromDate}
              viewByMonth
              onChange={(date) => setFromDate(date.startOf('month'))}
            />

            <DatePicker
              fullWidth={isMobile}
              label={intl.formatMessage({ id: `orderFeed.listOrderSupplyPlan.filter.toDateLabel` })}
              disableFuture={true}
              value={toDate}
              viewByMonth
              onChange={(date) => setToDate(date.endOf('month'))}
            />
          </FilterSidebar>
          <Paper key="paper_highcharts">
            <Typography
              variant="h4"
              sx={{
                padding: 4,
                fontFamily: 'DM Sans, Arial',
                textAlign: 'center',
                textTransform: 'none',
              }}>
              <FormattedMessage id={`${PAGE_NAME_ID}.chartTitle`} />
            </Typography>
            <Box display={'flex'} justifyContent={'flex-end'} padding={2}>
              <Button
                className="HighchartsButton"
                sx={{
                  margin: '0px 2rem',
                }}
                onClick={exportToCSV}>
                <FormattedMessage id={`general.exportCSVBtn`} />
              </Button>
            </Box>
            <HighchartsReact highcharts={Highcharts} ref={chartComponentRef} options={options} />
          </Paper>
          {renderProductRows().map((site, idx) => (
            <Collapsible
              key={`paper_${site.globalSiteId}`}
              label={site.name}
              hideBorder
              open={site.perGroupPurchasesHistory.length > 0 && idx === 0 ? true : false}>
              <List dark options={renderHeaders()}>
                {site.perGroupPurchasesHistory.map((group, idxx) => {
                  return (
                    <ListRow key={`${site.globalSiteId}_${idxx}_${group.name}`}>
                      <ListCell
                        key={`${site.globalSiteId}_${group.name}_cell`}
                        sx={
                          isMobile
                            ? { border: 'none' }
                            : {
                                position: 'sticky',
                              }
                        }>
                        {group.name}
                      </ListCell>
                      {renderHeaders()
                        .slice(1, -1)
                        .map((item, mtId) => (
                          <ListCell
                            sx={
                              item.id.includes('total') && isMobile
                                ? { textAlign: 'right', fontWeight: 700 }
                                : isMobile
                                  ? {}
                                  : { textAlign: 'right' }
                            }
                            key={`${site.globalSiteId}_monthly_total_${mtId}`}>
                            <FormattedNumber
                              value={
                                item.id.includes('total')
                                  ? group.monthlyTotals
                                      .filter((mt) => mt.year === Number(item.id.split('-')[0]))
                                      .reduce((yearlyTotal, mt) => yearlyTotal + mt.quantity, 0)
                                  : group.monthlyTotals.find(
                                      (mt) => mt.month === Number(item.id.split('-')[1]) && mt.year === Number(item.id.split('-')[0])
                                    )?.quantity || 0
                              }
                            />
                          </ListCell>
                        ))}
                      <ListCell
                        key={`${site.globalSiteId}_${group.name}_all_total`}
                        sx={
                          isMobile
                            ? {
                                textAlign: 'right',
                                border: 'none',
                                fontWeight: 700,
                              }
                            : {
                                textAlign: 'right',
                                backgroundColor: theme.palette.primary.light,
                              }
                        }>
                        <FormattedNumber value={group.total} />
                      </ListCell>
                    </ListRow>
                  );
                })}
                <ListRow key={`${site.globalSiteId}_totals`} options={renderHeaders()}>
                  <ListCell
                    key={`${site.globalSiteId}_total_cell`}
                    sx={!isMobile ? { position: 'sticky', left: '0', backgroundColor: theme.palette.primary.light } : {}}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.total`} />
                  </ListCell>
                  {renderHeaders()
                    .slice(1, -1) // trim list headers to only include months
                    .map((item) => (
                      <ListCell
                        key={`${site.globalSiteId}_${item.id}_monthly_total`}
                        sx={!isMobile ? { background: theme.palette.primary.light, textAlign: 'right' } : {}}>
                        <FormattedNumber
                          value={
                            item.id.includes('total')
                              ? totalYearlyQuantity(site, Number(item.id.split('-')[0]))
                              : totalMonthlyQuantity(site, item.id)
                          }
                        />
                      </ListCell>
                    ))}
                  <ListCell
                    key={`${site.globalSiteId}_all_total`}
                    sx={
                      isMobile
                        ? { padding: '5px 0 ' }
                        : { backgroundColor: theme.palette.primary.light, textAlign: 'right', padding: '0 10px ' }
                    }>
                    <FormattedNumber
                      value={_.uniq(monthYears.map((my) => my.split('-')[0])).reduce(
                        (total, year) => total + totalYearlyQuantity(site, Number(year)),
                        0
                      )}
                    />
                  </ListCell>
                </ListRow>
              </List>
            </Collapsible>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

const PurchaseHistoryContainer: React.FC = () => (
  <OrderFeedProvider>
    <PurchaseHistory />
  </OrderFeedProvider>
);

export default PurchaseHistoryContainer;
